// TEN18-F5 (docs/build-briefs/TEN18-F5-window-stacking-and-composition-snapshot.md; ADR-008 Questions 2 and 3, DEC-089):
// the words for a published discount's central date window and its stacking declaration. Pure, shared by the franchisor
// console (where they are chosen) and the location's Settings (where they are shown read-only). A window is whole calendar
// dates, so no time zone is ever applied to one here: the label is cut from the YYYY-MM-DD text, never through Date.

export type CompositionDto = "sequential" | "additive" | "exclusive";

export const COMPOSITION_OPTIONS: readonly { value: CompositionDto; label: string }[] = [
  { value: "sequential", label: "Stacks: applied after the member discount (20% + 10% = 28% off)" },
  { value: "additive", label: "Stacks: added to the member discount (20% + 10% = 30% off)" },
  { value: "exclusive", label: "Does not stack with the member discount" },
];

export const DEFAULT_COMPOSITION: CompositionDto = "sequential";

/** FRANFIX1: the member standing discount the stacking examples use. It is the shop default; a location can set its own, so the words say "on 10%". */
export const MEMBER_EXAMPLE_PERCENT = 10;

const trimmed = (n: number): string => String(Math.round(n * 100) / 100);

/** What a member pays off in total when a percentage discount of `percent` combines with the member percentage, or null for "does not stack". */
export function stackedPercent(composition: CompositionDto, percent: number, member = MEMBER_EXAMPLE_PERCENT): number | null {
  if (composition === "sequential") return 100 - ((100 - percent) * (100 - member)) / 100;
  if (composition === "additive") return Math.min(100, percent + member);
  return null;
}

/** The stacking choice in words with the typed percentage in the example. With no usable percentage it falls back to the 20% example. */
export function stackingChoiceLabel(composition: CompositionDto, typedPercent: number | null): string {
  const percent = typedPercent !== null && typedPercent > 0 && typedPercent <= 100 ? typedPercent : 20;
  const total = stackedPercent(composition, percent);
  if (total === null) return "Does not stack with the member discount";
  const how = composition === "sequential" ? "applied after" : "added to";
  return `Stacks: ${how} the member discount (${trimmed(percent)}% + ${MEMBER_EXAMPLE_PERCENT}% = ${trimmed(total)}% off)`;
}

export function isCompositionDto(value: unknown): value is CompositionDto {
  return value === "sequential" || value === "additive" || value === "exclusive";
}

export function compositionLabel(composition: CompositionDto): string {
  return COMPOSITION_OPTIONS.find((o) => o.value === composition)?.label ?? "-";
}

import { usDate } from "./dateText.ts";

const ISO_DATE = /^(\d{4})-(\d{2})-(\d{2})$/;

/** True for a YYYY-MM-DD string that is a real calendar date. */
export function isIsoDate(value: unknown): value is string {
  if (typeof value !== "string") return false;
  const m = ISO_DATE.exec(value);
  if (m === null) return false;
  const [y, mo, d] = [Number(m[1]), Number(m[2]), Number(m[3])];
  const check = new Date(Date.UTC(y, mo - 1, d));
  return check.getUTCFullYear() === y && check.getUTCMonth() === mo - 1 && check.getUTCDate() === d;
}

/** "Oct 20, 2026" from 2026-10-20, or a dash. */
export function windowDateLabel(iso: string | null): string {
  if (iso === null || ISO_DATE.exec(iso) === null) return "-";
  return usDate(iso) || "-";
}

/** OFFERWORDS1: the window line when there is no start or end date. */
export const OPEN_WINDOW_TEXT = "Dates: no start or end date";

/** The window in one line: "Dates: Oct 20, 2026 to Oct 31, 2026", "Dates: starts Oct 20, 2026", "Dates: ends Oct 31, 2026", or OPEN_WINDOW_TEXT when open. */
export function windowText(startsOn: string | null, endsOn: string | null): string {
  if (startsOn === null && endsOn === null) return OPEN_WINDOW_TEXT;
  if (startsOn !== null && endsOn !== null) return `Dates: ${windowDateLabel(startsOn)} to ${windowDateLabel(endsOn)}`;
  return startsOn !== null ? `Dates: starts ${windowDateLabel(startsOn)}` : `Dates: ends ${windowDateLabel(endsOn)}`;
}
